import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Folder, FolderOpen, Database, Upload, FileText, BarChart3, Users, HardDrive, Eye, Clipboard, Code, FileCheck, Download, Trash2, Search, ChevronRight, ArrowLeft, Archive, Share2, Layers, FileType2, Image as ImageIcon, Table2, File, Sparkles } from 'lucide-react';
import FilePreviewModal from './files/FilePreviewModal';
import FileActionButtons from './files/FileActions';
import { toast, confirm as confirmModal } from './ui/Toast';
import ExpertiseBasesPanel from './expertise/ExpertiseBasesPanel';

const FilesInterface = ({ user, projectId, onFilesChanged, onFileAnalyze }) => {
console.log('FilesInterface reçoit projectId:', projectId);
  const [activeTab, setActiveTab] = useState('projects');
const [projectFiles, setProjectFiles] = useState([]);
const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);
const [totalFiles, setTotalFiles] = useState(0);
const [itemsPerPage] = useState(20);
const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    storage: 'all',
    search: ''
  });

  // États pour upload multi-fichiers
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadFolderId, setUploadFolderId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, results: [] });

  // États folder navigation (Session 29)
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [currentBreadcrumb, setCurrentBreadcrumb] = useState([]);
  const [subfolders, setSubfolders] = useState([]);
  const [folderTree, setFolderTree] = useState([]);
  // S59 P3 — fichiers globaux du projet (pour les compteurs stats indépendants du folder courant)
  const [globalProjectFiles, setGlobalProjectFiles] = useState([]);

const loadProjectFiles = async () => {
  setLoading(true);
  try {
    const token = localStorage.getItem('oracle_token');
    // S59 P3 fix : à la racine, passer folder_id=root sinon le backend renvoie TOUS les fichiers du projet (pas filtré).
    const folderParam = currentFolderId !== null ? `&folder_id=${currentFolderId}` : '&folder_id=root';
    const response = await axios.get(`/api/projects/${projectId}/files?page=${currentPage}&limit=${itemsPerPage}${folderParam}`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    if (response.data && response.data.files) {
      const adaptedFiles = response.data.files.map(file => ({
        id: file.id,
        name: file.original_name,
        category: 'Projet',
        storage: file.storage_status === 'nextcloud' ? 'nextcloud' : 'local',
        size: file.file_size ? (parseInt(file.file_size) < 1024 ? `${file.file_size} octets` : `${Math.round(parseInt(file.file_size) / 1024)} KB`) :
              file.size ? (parseInt(file.size) < 1024 ? `${file.size} octets` : `${Math.round(parseInt(file.size) / 1024)} KB`) : 'N/A',
        upload_date: new Date(file.uploaded_at).toLocaleDateString('fr-FR'),
        isShared: !!(file.upload_metadata?.shared_from)
      }));
      setProjectFiles(adaptedFiles);
      if (response.data.pagination) {
        setTotalPages(response.data.pagination.total_pages);
        setTotalFiles(response.data.pagination.total_files);
      }
    }

    // S59 P3 — fetch séparé tous les fichiers du projet (avec folder_id) pour calculer
    // les stats récursives : à la racine = projet entier, dans un dossier = sous-arbre complet.
    try {
      const allRes = await axios.get(`/api/projects/${projectId}/files?page=1&limit=500&folder_id=all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (allRes.data?.files) {
        setGlobalProjectFiles(allRes.data.files.map(file => ({
          id: file.id,
          name: file.original_name,
          folder_id: file.folder_id,
          storage: file.storage_status === 'nextcloud' ? 'nextcloud' : 'local',
        })));
      }
    } catch (e) { /* silencieux — fallback sur projectFiles */ }
  } catch (error) {
    console.error('Erreur chargement fichiers projets:', error);
    setProjectFiles([]);
  } finally {
    setLoading(false);
  }
};

  // ── Folder context (Session 29) ───────────────────
  const loadFolderContext = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await axios.get(`/api/projects/${projectId}/folders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success) {
        const tree = res.data.tree || [];
        setFolderTree(tree);
        // Extract subfolders of currentFolderId
        if (currentFolderId === null) {
          setSubfolders(tree);
        } else {
          const findNode = (nodes) => {
            for (const n of nodes) {
              if (n.id === currentFolderId) return n.children || [];
              if (n.children) {
                const found = findNode(n.children);
                if (found) return found;
              }
            }
            return null;
          };
          setSubfolders(findNode(tree) || []);
        }
      }
      // Load breadcrumb if in a subfolder
      if (currentFolderId !== null) {
        const bcRes = await axios.get(`/api/projects/${projectId}/folders/${currentFolderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (bcRes.data.success && bcRes.data.breadcrumb) {
          setCurrentBreadcrumb(bcRes.data.breadcrumb);
        }
      } else {
        setCurrentBreadcrumb([]);
      }
    } catch (e) {
      console.error('Erreur chargement contexte dossiers:', e);
    }
  };

  const navigateToFolder = (folderId) => {
    setCurrentFolderId(folderId);
    setCurrentPage(1);
  };

  const navigateUp = () => {
    if (currentBreadcrumb.length > 1) {
      navigateToFolder(currentBreadcrumb[currentBreadcrumb.length - 2].id);
    } else {
      navigateToFolder(null);
    }
  };

  const flattenTree = (nodes, depth = 0) => {
    let result = [];
    for (const n of nodes) {
      result.push({ id: n.id, name: '\u00A0\u00A0'.repeat(depth) + n.name, depth });
      if (n.children) result.push(...flattenTree(n.children, depth + 1));
    }
    return result;
  };
  // Fonction upload multi-fichiers
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    const total = selectedFiles.length;
    setUploadProgress({ current: 0, total, results: [] });

    const token = localStorage.getItem('oracle_token');
    const results = [];

    // Envoyer tous les fichiers en un seul appel (multer array max 10)
    try {
      const formData = new FormData();
      for (const file of selectedFiles) {
        formData.append('files', file);
      }
      if (uploadFolderId) formData.append('folder_id', uploadFolderId);

      const response = await axios.post(`/api/projects/${projectId}/upload/files`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const pct = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(prev => ({ ...prev, uploadPct: pct }));
          }
        }
      });

      if (response.data.success) {
        // Mapper les résultats par fichier
        const serverResults = response.data.results || [];
        for (let i = 0; i < selectedFiles.length; i++) {
          const sr = serverResults[i];
          results.push({
            name: selectedFiles[i].name,
            success: sr ? !sr.error : true,
            error: sr?.error || null
          });
        }
      } else {
        // Erreur globale
        for (const file of selectedFiles) {
          results.push({ name: file.name, success: false, error: response.data.error });
        }
      }
    } catch (error) {
      console.error('Erreur upload:', error);
      const msg = error.response?.data?.error || error.message || 'Connexion perdue';
      for (const file of selectedFiles) {
        results.push({ name: file.name, success: false, error: msg });
      }
    }

    setUploadProgress({ current: total, total, results });

    const successes = results.filter(r => r.success).length;
    const failures = results.filter(r => !r.success).length;

    if (successes > 0) {
      loadProjectFiles();
      if (onFilesChanged) onFilesChanged();
    }

    // Garder les résultats visibles 5s puis reset
    setTimeout(() => {
      if (failures === 0) {
        setSelectedFiles([]);
        setUploadProgress({ current: 0, total: 0, results: [] });
      }
      setUploading(false);
    }, failures > 0 ? 8000 : 3000);
  };

  const [previewFile, setPreviewFile] = useState(null);

  const handleDeleteFile = async (fileId, fileName) => {
    if (!await confirmModal(`Supprimer "${fileName}" ?`, { danger: true })) return;
    try {
      const token = localStorage.getItem('oracle_token');
      await axios.delete(`/api/projects/${projectId}/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjectFiles(prev => prev.filter(f => f.id !== fileId));
      setTotalFiles(prev => prev - 1);
      if (onFilesChanged) onFilesChanged();
    } catch (error) {
      console.error('Erreur suppression fichier:', error);
      toast.error('Erreur suppression: ' + (error.response?.data?.error || error.message));
    }
  };

  useEffect(() => {
    if (activeTab === 'projects') {
      loadProjectFiles();
      loadFolderContext();
    }
  }, [activeTab, currentPage, projectId, currentFolderId]);

  // Filtrage des fichiers projet (Phase A — filtre score retiré, réintégré en V2 via scoring multi-dimensionnel)
const filteredProjectFiles = projectFiles.filter(file => {
   if (filters.category !== 'all') {
      const ext = file.name.split('.').pop().toLowerCase();
      const getFileCategory = (ext) => {
        if (['pdf', 'doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'Doc';
        if (['txt', 'md'].includes(ext)) return 'Txt';
        if (['json', 'csv', 'xml'].includes(ext)) return 'Data';
        if (['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext)) return 'Img';
        return 'Autre';
      };
     if (getFileCategory(ext) !== filters.category) return false;
    }
    if (filters.storage !== 'all' && file.storage !== filters.storage) return false;
    if (filters.search && !file.name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });
  // ─── Composant Upload & Paste Premium ───────────────────────
  const UploadPasteTab = ({ projectId, user, selectedFiles, setSelectedFiles, uploadFolderId, setUploadFolderId, uploading, handleUpload, uploadProgress }) => {
    const [dragActive, setDragActive] = React.useState(false);
    const [pasteContent, setPasteContent] = React.useState('');
    const [pasteType, setPasteType] = React.useState(null);
    const [pasteName, setPasteName] = React.useState('');
    const [pasteSaving, setPasteSaving] = React.useState(false);
    const fileInputRef = React.useRef(null);

    // ZIP states (Session 29)
    const [zipFile, setZipFile] = React.useState(null);
    const [zipDragActive, setZipDragActive] = React.useState(false);
    const [zipImporting, setZipImporting] = React.useState(false);
    const [zipProgress, setZipProgress] = React.useState(null);
    const [zipResults, setZipResults] = React.useState(null);
    const [zipTargetFolder, setZipTargetFolder] = React.useState('');
    const [zipFolders, setZipFolders] = React.useState([]);
    const zipInputRef = React.useRef(null);

    // Load folders for ZIP destination selector
    React.useEffect(() => {
      const loadZipFolders = async () => {
        try {
          const token = localStorage.getItem('oracle_token');
          const res = await axios.get(`/api/projects/${projectId}/folders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data.success) setZipFolders(flattenTree(res.data.tree || []));
        } catch (e) { /* silently fail */ }
      };
      loadZipFolders();
    }, [projectId]);

    const handleZipImport = async () => {
      if (!zipFile || zipImporting) return;
      setZipImporting(true);
      setZipProgress({ current: 0, total: 0, label: 'Démarrage...' });
      setZipResults(null);

      try {
        const token = localStorage.getItem('oracle_token');
        const formData = new FormData();
        formData.append('zipfile', zipFile);
        if (zipTargetFolder) formData.append('parent_folder_id', zipTargetFolder);

        const response = await fetch(`/api/projects/${projectId}/upload/zip`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop();

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'progress') {
                setZipProgress({ current: data.current, total: data.total, label: data.label || data.filename });
              } else if (data.type === 'done') {
                setZipResults({
                  files_imported: data.files_imported || 0,
                  folders_created: data.folders_created || 0,
                  files_errors: data.files_errors || 0
                });
                if (onFilesChanged) onFilesChanged();
                loadProjectFiles();
              } else if (data.type === 'error') {
                toast.error(data.message || 'Erreur import ZIP');
              } else if (data.type === 'warning') {
                toast.warning(data.message);
              }
            } catch (e) { /* parse error, skip */ }
          }
        }
      } catch (e) {
        toast.error('Erreur import ZIP: ' + (e.message || 'connexion perdue'));
      } finally {
        setZipImporting(false);
        setZipProgress(null);
      }
    };

    const detectType = (text) => {
      if (!text.trim()) return null;
      if (text.includes('function') || text.includes('=>') || text.includes('const ') || text.includes('import ')) return { type: 'javascript', ext: 'js', color: '#f7df1e' };
      if (text.includes('<html>') || text.includes('<!DOCTYPE') || text.includes('<div')) return { type: 'html', ext: 'html', color: '#e34c26' };
      if (text.includes('# ') || text.includes('## ') || text.includes('```')) return { type: 'markdown', ext: 'md', color: '#083fa1' };
      if (text.toUpperCase().includes('SELECT') || text.toUpperCase().includes('CREATE TABLE')) return { type: 'sql', ext: 'sql', color: '#336791' };
      const csvLines = text.split('\n').filter(l => l.trim() && l.includes(','));
      if (csvLines.length >= 3) {
        const counts = csvLines.slice(0, 5).map(l => l.split(',').length);
        if (counts.every(c => c === counts[0] && c >= 3)) return { type: 'csv', ext: 'csv', color: '#16a34a' };
      }
      return { type: 'text', ext: 'txt', color: '#6b7280' };
    };

    const handlePasteChange = (text) => {
      setPasteContent(text);
      const t = detectType(text);
      setPasteType(t);
      if (t) {
        const ts = new Date().toISOString().slice(0,16).replace('T', '_');
        setPasteName(`itriia_paste_${ts}.${t.ext}`);
      }
    };

    const handlePasteSave = async () => {
      if (!pasteContent.trim() || !pasteType) return;
      setPasteSaving(true);
      try {
        const token = localStorage.getItem('oracle_token');
        const res = await fetch(`/api/projects/${projectId}/paste-as-file`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ content: pasteContent, filename: pasteName, contentType: pasteType.type })
        });
        if (res.ok) {
          setPasteContent(''); setPasteType(null); setPasteName('');
          if (onFilesChanged) onFilesChanged();
          loadProjectFiles();
        }
      } catch (e) { console.error('Erreur paste:', e); }
      finally { setPasteSaving(false); }
    };

    const handlePasteDownload = () => {
      if (!pasteContent.trim()) return;
      const name = pasteName ? pasteName.replace(/\.[^.]+$/, '.md') : 'paste.md';
      const blob = new Blob([pasteContent], { type: 'text/markdown' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; a.click();
    };

    const cardStyle = {
      background: 'var(--card-bg)', border: '1px solid var(--card-border)',
      borderRadius: '12px', padding: '24px'
    };

    const headingStyle = {
      fontSize: '15px', fontWeight: '600', color: 'var(--text)',
      display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px'
    };

    const iconBox = (color) => ({
      width: '36px', height: '36px', borderRadius: '10px',
      background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center'
    });

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', gap: '20px' }}>
        {/* ── Upload Fichier ─────────────────────── */}
        <div style={cardStyle}>
          <div style={headingStyle}>
            <div style={iconBox('#2563eb')}><Upload size={18} style={{ color: '#2563eb' }} /></div>
            Upload Fichier
          </div>

          {/* Drop Zone Multi-fichiers */}
          <div
            onDragOver={e => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={() => setDragActive(false)}
            onDrop={e => { e.preventDefault(); setDragActive(false); const newFiles = Array.from(e.dataTransfer.files); if (newFiles.length) setSelectedFiles(prev => [...prev, ...newFiles].slice(0, 10)); }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragActive ? '#2563eb' : 'var(--card-border)'}`,
              borderRadius: '10px', padding: selectedFiles.length > 0 ? '16px' : '32px', textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.2s',
              background: dragActive ? 'rgba(37,99,235,0.04)' : 'var(--input-bg)'
            }}
          >
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => { const newFiles = Array.from(e.target.files); if (newFiles.length) { setSelectedFiles(prev => [...prev, ...newFiles].slice(0, 10)); e.target.value = ''; } }} disabled={uploading} />
            <Upload size={24} style={{ color: dragActive ? '#2563eb' : 'var(--text-secondary)', margin: '0 auto 6px', display: 'block' }} />
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>
              {selectedFiles.length > 0 ? `${selectedFiles.length} fichier${selectedFiles.length > 1 ? 's' : ''} sélectionné${selectedFiles.length > 1 ? 's' : ''}` : 'Glisser des fichiers ici'}
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
              {selectedFiles.length > 0 ? 'Glisser ou cliquer pour en ajouter (max 10)' : 'ou cliquer pour parcourir • multi-sélection activée'}
            </div>
          </div>

          {/* Liste fichiers sélectionnés */}
          {selectedFiles.length > 0 && !uploading && (
            <div style={{ marginTop: '12px', maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {selectedFiles.map((file, idx) => (
                <div key={`${file.name}-${idx}`} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '8px 12px', background: 'var(--input-bg)',
                  border: '1px solid var(--card-border)', borderRadius: '8px', fontSize: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                    <FileText size={14} style={{ color: '#2563eb', flexShrink: 0 }} />
                    <span style={{ color: 'var(--text)', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</span>
                    <span style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>{Math.round(file.size / 1024)} KB</span>
                  </div>
                  <button onClick={(e) => { e.stopPropagation(); setSelectedFiles(prev => prev.filter((_, i) => i !== idx)); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px', color: 'var(--text-secondary)', display: 'flex' }}
                    title="Retirer">
                    <Trash2 size={13} style={{ color: '#ef4444' }} />
                  </button>
                </div>
              ))}
              {selectedFiles.length > 1 && (
                <div style={{ textAlign: 'right', marginTop: '4px' }}>
                  <button onClick={(e) => { e.stopPropagation(); setSelectedFiles([]); }}
                    style={{ fontSize: '11px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>
                    Tout retirer
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Barre de progression upload */}
          {uploading && uploadProgress.total > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span style={{ color: 'var(--text)', fontWeight: '500' }}>
                  Upload en cours...
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {uploadProgress.uploadPct ? `${uploadProgress.uploadPct}%` : ''}
                </span>
              </div>
              <div style={{ height: '6px', background: 'var(--card-border)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '3px', transition: 'width 0.3s',
                  background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
                  width: uploadProgress.uploadPct ? `${uploadProgress.uploadPct}%` : '0%'
                }} />
              </div>
            </div>
          )}

          {/* Résultats upload par fichier */}
          {uploadProgress.results.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text)', marginBottom: '4px' }}>
                Résultats — {uploadProgress.results.filter(r => r.success).length}/{uploadProgress.results.length} réussis
              </div>
              {uploadProgress.results.map((r, idx) => (
                <div key={idx} style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '6px 10px', borderRadius: '6px', fontSize: '12px',
                  background: r.success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${r.success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`
                }}>
                  <FileCheck size={13} style={{ color: r.success ? '#22c55e' : '#ef4444', flexShrink: 0 }} />
                  <span style={{ color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                  <span style={{ color: r.success ? '#22c55e' : '#ef4444', fontWeight: '500', fontSize: '11px', flexShrink: 0 }}>
                    {r.success ? '✓' : r.error || 'Erreur'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Phase A (11 avril 2026) — Sélecteur de destination retiré.
              Les uploads vont directement dans le projet courant.
              La V2 base d'expertise réintégrera un picker enrichi. */}

          {/* Dossier destination (Session 29) */}
          {zipFolders.length > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '6px' }}>Dossier</div>
              <select value={uploadFolderId} onChange={e => setUploadFolderId(e.target.value)} disabled={uploading}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                  color: 'var(--text)', fontSize: '13px'
                }}>
                <option value="">Racine du projet</option>
                {zipFolders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Bouton Upload */}
          <button onClick={(e) => { e.stopPropagation(); handleUpload(); }} disabled={selectedFiles.length === 0 || uploading}
            style={{
              marginTop: '16px', width: '100%', padding: '12px',
              background: selectedFiles.length === 0 || uploading ? 'var(--card-border)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '13px', fontWeight: '600', cursor: selectedFiles.length === 0 || uploading ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
            <Upload size={14} />
            {uploading ? `Upload en cours...` :
             selectedFiles.length === 0 ? 'Sélectionnez des fichiers' :
             selectedFiles.length === 1 ? `Uploader ${selectedFiles[0].name}` :
             `Uploader ${selectedFiles.length} fichiers`}
          </button>

          {/* Info */}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['PDF', 'Word', 'Excel', 'Images', 'Texte', 'Code'].map(t => (
              <span key={t} style={{
                fontSize: '10px', padding: '3px 8px', borderRadius: '4px',
                background: 'var(--input-bg)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)'
              }}>{t}</span>
            ))}
          </div>
        </div>

        {/* ── Paste as File ─────────────────────── */}
        <div style={cardStyle}>
          <div style={headingStyle}>
            <div style={iconBox('#1D4ED8')}><Clipboard size={18} style={{ color: '#1D4ED8' }} /></div>
            Paste as File
            <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '400' }}>Auto-détection type</span>
          </div>

          {/* Textarea */}
          <textarea
            value={pasteContent}
            onChange={e => handlePasteChange(e.target.value)}
            onPaste={e => { const t = e.clipboardData.getData('text'); if (t) { e.preventDefault(); handlePasteChange(t); } }}
            placeholder={'Collez votre contenu ici...\n• Code JavaScript / Python / SQL\n• Documents Markdown\n• Données CSV / JSON'}
            style={{
              width: '100%', minHeight: '140px', maxHeight: '220px', resize: 'vertical',
              border: '1px dashed var(--card-border)', borderRadius: '10px',
              padding: '14px', fontSize: '12px', fontFamily: "'Monaco', 'Menlo', monospace",
              background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
              transition: 'border-color 0.2s'
            }}
            onFocus={e => e.target.style.borderColor = '#1D4ED8'}
            onBlur={e => e.target.style.borderColor = 'var(--card-border)'}
          />

          {/* Détection + nom fichier */}
          {pasteType && pasteContent.trim() && (
            <div style={{
              marginTop: '12px', padding: '10px 14px', background: 'var(--input-bg)',
              border: '1px solid var(--card-border)', borderRadius: '8px',
              display: 'flex', alignItems: 'center', gap: '10px'
            }}>
              <span style={{
                fontSize: '11px', fontWeight: '700', textTransform: 'uppercase',
                color: pasteType.color, display: 'flex', alignItems: 'center', gap: '4px'
              }}>
                <Code size={12} />
                {pasteType.type}
              </span>
              <input
                value={pasteName} onChange={e => setPasteName(e.target.value)}
                style={{
                  flex: 1, border: '1px solid var(--card-border)', borderRadius: '6px',
                  padding: '6px 10px', fontSize: '12px', background: 'var(--card-bg)',
                  color: 'var(--text)', outline: 'none'
                }}
              />
            </div>
          )}

          {/* Actions */}
          {pasteContent.trim() && (
            <div style={{ marginTop: '12px', display: 'flex', gap: '8px' }}>
              <button onClick={handlePasteSave} disabled={pasteSaving || !pasteContent.trim()}
                style={{
                  flex: 1, padding: '12px',
                  background: pasteSaving ? 'var(--card-border)' : 'linear-gradient(135deg, #1D4ED8, #1e40af)',
                  color: 'white', border: 'none', borderRadius: '10px',
                  fontSize: '13px', fontWeight: '600', cursor: pasteSaving ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                }}>
                <FileCheck size={14} />
                {pasteSaving ? 'Sauvegarde...' : 'Sauver comme fichier'}
              </button>
              <button onClick={handlePasteDownload}
                style={{
                  padding: '12px 16px', background: 'var(--input-bg)',
                  border: '1px solid var(--card-border)', borderRadius: '10px',
                  fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '4px'
                }}>
                <Download size={12} /> .md
              </button>
              <button onClick={() => { setPasteContent(''); setPasteType(null); setPasteName(''); }}
                style={{
                  padding: '12px 16px', background: 'var(--input-bg)',
                  border: '1px solid var(--card-border)', borderRadius: '10px',
                  fontSize: '12px', color: 'var(--text-secondary)', cursor: 'pointer'
                }}>
                Vider
              </button>
            </div>
          )}
        </div>

        {/* ── Import ZIP (Session 29) ─────────────────── */}
        <div style={cardStyle}>
          <div style={headingStyle}>
            <div style={iconBox('#7c3aed')}><Archive size={18} style={{ color: '#7c3aed' }} /></div>
            Import ZIP
            <span style={{ fontSize: '10px', color: 'var(--text-secondary)', fontWeight: '400' }}>Arborescence auto</span>
          </div>

          {/* Drop Zone ZIP */}
          <div
            onDragOver={e => { e.preventDefault(); setZipDragActive(true); }}
            onDragLeave={() => setZipDragActive(false)}
            onDrop={e => {
              e.preventDefault(); setZipDragActive(false);
              const f = Array.from(e.dataTransfer.files).find(f => f.name.endsWith('.zip'));
              if (f) { setZipFile(f); setZipResults(null); }
              else toast.warning('Seuls les fichiers .zip sont acceptés');
            }}
            onClick={() => zipInputRef.current?.click()}
            style={{
              border: `2px dashed ${zipDragActive ? '#7c3aed' : 'var(--card-border)'}`,
              borderRadius: '10px', padding: zipFile ? '16px' : '32px', textAlign: 'center',
              cursor: 'pointer', transition: 'all 0.2s',
              background: zipDragActive ? 'rgba(124,58,237,0.04)' : 'var(--input-bg)'
            }}
          >
            <input ref={zipInputRef} type="file" accept=".zip" style={{ display: 'none' }}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) { setZipFile(f); setZipResults(null); }
                e.target.value = '';
              }} disabled={zipImporting} />

            {zipFile ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                <Archive size={20} style={{ color: '#7c3aed' }} />
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>{zipFile.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{Math.round(zipFile.size / 1024)} KB</div>
                </div>
                <button onClick={e => { e.stopPropagation(); setZipFile(null); setZipResults(null); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#ef4444', display: 'flex' }}>
                  <Trash2 size={14} />
                </button>
              </div>
            ) : (
              <>
                <Archive size={24} style={{ color: zipDragActive ? '#7c3aed' : 'var(--text-secondary)', margin: '0 auto 6px', display: 'block' }} />
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)' }}>Glisser un fichier .zip ici</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                  ou cliquer pour parcourir • max 50 fichiers, 200 Mo
                </div>
              </>
            )}
          </div>

          {/* Dossier destination */}
          {zipFile && !zipImporting && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-secondary)', marginBottom: '6px' }}>Dossier destination</div>
              <select value={zipTargetFolder} onChange={e => setZipTargetFolder(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: '8px',
                  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                  color: 'var(--text)', fontSize: '13px'
                }}>
                <option value="">Racine du projet</option>
                {zipFolders.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Progress bar */}
          {zipImporting && zipProgress && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                <span style={{ color: 'var(--text)', fontWeight: '500' }}>
                  {zipProgress.label || 'Import en cours...'}
                </span>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {zipProgress.total > 0 ? `${zipProgress.current}/${zipProgress.total}` : ''}
                </span>
              </div>
              <div style={{ height: '6px', background: 'var(--card-border)', borderRadius: '3px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '3px', transition: 'width 0.3s',
                  background: 'linear-gradient(90deg, #7c3aed, #a855f7)',
                  width: zipProgress.total > 0 ? `${Math.round((zipProgress.current / zipProgress.total) * 100)}%` : '30%'
                }} />
              </div>
            </div>
          )}

          {/* Results */}
          {zipResults && (
            <div style={{
              marginTop: '12px', padding: '12px 14px',
              background: zipResults.files_errors > 0 ? 'rgba(239,68,68,0.06)' : 'rgba(34,197,94,0.06)',
              border: `1px solid ${zipResults.files_errors > 0 ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)'}`,
              borderRadius: '8px', fontSize: '12px'
            }}>
              <div style={{ fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Import terminé</div>
              <div style={{ color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {zipResults.files_imported > 0 && <div>✓ {zipResults.files_imported} fichier{zipResults.files_imported > 1 ? 's' : ''} importé{zipResults.files_imported > 1 ? 's' : ''}</div>}
                {zipResults.folders_created > 0 && <div>✓ {zipResults.folders_created} dossier{zipResults.folders_created > 1 ? 's' : ''} créé{zipResults.folders_created > 1 ? 's' : ''}</div>}
                {zipResults.files_errors > 0 && <div style={{ color: '#ef4444' }}>✗ {zipResults.files_errors} erreur{zipResults.files_errors > 1 ? 's' : ''}</div>}
              </div>
            </div>
          )}

          {/* Import button */}
          <button onClick={(e) => { e.stopPropagation(); handleZipImport(); }} disabled={!zipFile || zipImporting}
            style={{
              marginTop: '16px', width: '100%', padding: '12px',
              background: !zipFile || zipImporting ? 'var(--card-border)' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '13px', fontWeight: '600', cursor: !zipFile || zipImporting ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
            <Archive size={14} />
            {zipImporting ? 'Import en cours...' : zipFile ? `Importer ${zipFile.name}` : 'Sélectionnez un fichier ZIP'}
          </button>

          {/* Info */}
          <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {['Structure dossiers', 'Max 50 fichiers', '200 Mo max'].map(t => (
              <span key={t} style={{
                fontSize: '10px', padding: '3px 8px', borderRadius: '4px',
                background: 'var(--input-bg)', border: '1px solid var(--card-border)', color: 'var(--text-secondary)'
              }}>{t}</span>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white text-gray-900 h-full overflow-auto">
      {/* Header avec bouton Organiser IA (S59 P3) */}
      <div className="border-b border-gray-200 p-4" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h3 className="text-lg font-medium text-gray-900">Gestion des Fichiers</h3>
          <p className="text-gray-600 text-sm mt-1">Consultation Base Expertise Métier et fichiers projets</p>
        </div>
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('open-file-organizer', { detail: { projectId } }))}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 8,
            background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
            border: '1px solid #6d28d9',
            color: '#ffffff', fontSize: 12.5, fontWeight: 600,
            cursor: 'pointer', flexShrink: 0,
            boxShadow: '0 2px 6px rgba(139, 92, 246, 0.25)',
          }}
          title="Réorganise les fichiers du projet en dossiers thématiques"
        >
          <Sparkles size={14} />
          Organiser avec l'IA
        </button>
      </div>

      {/* Onglets navigation premium — bleu nuit ITRIIA */}
      <div style={{
        padding: '12px 16px 10px',
      }}>
        <nav style={{
          display: 'flex', gap: 2,
          background: 'rgba(139, 92, 246, 0.04)',
          borderRadius: 12,
          padding: 3,
          border: 'none',
        }}>
          {[
            { key: 'projects', label: 'Fichiers Projets', Icon: Folder },
            { key: 'upload', label: 'Envoi Fichiers Multi', Icon: Upload },
            { key: 'expertise', label: 'Bases Expertises', Icon: Database },
          ].map(({ key, label, Icon }) => {
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                style={{
                  flex: '1 1 0',
                  padding: '9px 10px',
                  fontSize: 12,
                  minWidth: 0,
                  fontWeight: isActive ? 600 : 500,
                  letterSpacing: isActive ? '0.01em' : 0,
                  color: isActive ? '#1e40af' : 'var(--text-secondary)',
                  background: isActive
                    ? 'var(--card-bg, white)'
                    : 'transparent',
                  border: 'none',
                  borderRadius: 10,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  transition: 'all 0.2s ease',
                  boxShadow: isActive
                    ? '0 1px 3px rgba(30, 64, 175, 0.12), 0 0 0 1px rgba(30, 64, 175, 0.08)'
                    : 'none',
                  position: 'relative',
                }}
                onMouseEnter={e => {
                  if (!isActive) e.currentTarget.style.color = 'var(--text)';
                }}
                onMouseLeave={e => {
                  if (!isActive) e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                <Icon size={14} style={{ opacity: isActive ? 1 : 0.6 }} />
                {label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Contenu */}
      <div className="flex-1 p-4">
        {activeTab === 'expertise' && (
          <ExpertiseBasesPanel user={user} />
        )}

       {activeTab === 'projects' && (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

   {/* Breadcrumb navigation (Session 29) */}
   {currentFolderId !== null && (
     <div style={{
       display: 'flex', alignItems: 'center', gap: 6,
       padding: '10px 16px',
       background: 'var(--card-bg)', borderRadius: 10,
       border: '1px solid var(--card-border)', fontSize: 12
     }}>
       <button onClick={() => navigateToFolder(null)}
         style={{
           background: 'none', border: 'none', cursor: 'pointer',
           color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4,
           padding: '2px 6px', borderRadius: 4, fontSize: 12
         }}
         onMouseEnter={e => e.currentTarget.style.color = '#3b82f6'}
         onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
       >
         <Folder size={13} /> Racine
       </button>
       {currentBreadcrumb.map((crumb, i) => (
         <React.Fragment key={crumb.id}>
           <ChevronRight size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
           <button onClick={() => i < currentBreadcrumb.length - 1 ? navigateToFolder(crumb.id) : null}
             style={{
               background: 'none', border: 'none', fontSize: 12,
               cursor: i < currentBreadcrumb.length - 1 ? 'pointer' : 'default',
               color: i < currentBreadcrumb.length - 1 ? 'var(--text-secondary)' : '#3b82f6',
               fontWeight: i === currentBreadcrumb.length - 1 ? 600 : 400,
               padding: '2px 4px', borderRadius: 4
             }}
             onMouseEnter={e => { if (i < currentBreadcrumb.length - 1) e.currentTarget.style.color = '#3b82f6'; }}
             onMouseLeave={e => { if (i < currentBreadcrumb.length - 1) e.currentTarget.style.color = 'var(--text-secondary)'; }}
           >
             {crumb.name}
           </button>
         </React.Fragment>
       ))}
     </div>
   )}

   {/* Statistiques */}
{(() => {
  const getFileCategory = (ext) => {
    if (['pdf', 'doc', 'docx', 'odt', 'rtf'].includes(ext)) return 'Doc';
    if (['txt', 'md'].includes(ext)) return 'Txt';
    if (['json', 'csv', 'xml'].includes(ext)) return 'Data';
    if (['png', 'jpg', 'jpeg', 'gif', 'svg'].includes(ext)) return 'Img';
    return 'Autre';
  };

  // S59 P3 — stats compteurs récursives :
  //   - Racine : tout le projet (globalProjectFiles).
  //   - Dossier intermédiaire/feuille : tous les fichiers du sous-arbre (descendants inclus).
  const collectDescendantIds = (nodes, targetId) => {
    for (const n of (nodes || [])) {
      if (n.id === targetId) {
        const ids = new Set([n.id]);
        const walk = (children) => {
          for (const c of (children || [])) {
            ids.add(c.id);
            walk(c.children);
          }
        };
        walk(n.children);
        return ids;
      }
      const found = collectDescendantIds(n.children, targetId);
      if (found) return found;
    }
    return null;
  };

  let statsSource;
  if (globalProjectFiles.length === 0) {
    statsSource = projectFiles;
  } else if (currentFolderId === null) {
    statsSource = globalProjectFiles;
  } else {
    const descendantIds = collectDescendantIds(folderTree, currentFolderId);
    statsSource = descendantIds
      ? globalProjectFiles.filter(f => descendantIds.has(f.folder_id))
      : projectFiles;
  }

  const fileTypes = {};
  statsSource.forEach(file => {
    const ext = file.name.split('.').pop().toLowerCase();
    const cat = getFileCategory(ext);
    fileTypes[cat] = (fileTypes[cat] || 0) + 1;
  });

  const nextcloudCount = statsSource.filter(f => f.storage === 'nextcloud').length;

  // Palette & icônes par type (Session 35 — refonte visuelle card Types)
  const TYPE_META = {
    Doc:   { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)',  border: 'rgba(59,130,246,0.28)',  Icon: FileType2, label: 'Docs'   },
    Txt:   { color: '#22c55e', bg: 'rgba(34,197,94,0.12)',   border: 'rgba(34,197,94,0.28)',   Icon: FileText,  label: 'Texte'  },
    Data:  { color: '#eab308', bg: 'rgba(234,179,8,0.14)',   border: 'rgba(234,179,8,0.30)',   Icon: Table2,    label: 'Data'   },
    Img:   { color: '#ec4899', bg: 'rgba(236,72,153,0.12)',  border: 'rgba(236,72,153,0.28)',  Icon: ImageIcon, label: 'Images' },
    Autre: { color: '#94a3b8', bg: 'rgba(148,163,184,0.12)', border: 'rgba(148,163,184,0.28)', Icon: File,      label: 'Autre'  },
  };
  const TYPE_ORDER = ['Doc', 'Txt', 'Data', 'Img', 'Autre'];
  const typeEntries = TYPE_ORDER
    .filter(k => fileTypes[k] > 0)
    .map(k => ({ key: k, count: fileTypes[k], ...TYPE_META[k] }));

  const statCard = { background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 10, padding: 16 };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
      <div style={statCard}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <FileText size={22} style={{ color: '#3b82f6' }} />
          <div style={{ marginLeft: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{currentFolderId ? 'Fichiers (dossier)' : 'Total Fichiers'}</p>
            <p style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{statsSource.length}</p>
          </div>
        </div>
      </div>
      <div style={statCard}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <HardDrive size={22} style={{ color: '#22c55e' }} />
          <div style={{ marginLeft: 12 }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)' }}>ITRIIA Cloud</p>
            <p style={{ fontSize: 18, fontWeight: 600, color: 'var(--text)' }}>{nextcloudCount}</p>
          </div>
        </div>
      </div>
      <div style={statCard}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <Layers size={22} style={{ color: '#a855f7', flexShrink: 0, marginTop: 2 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Répartition par type</p>
            {typeEntries.length === 0 ? (
              <p style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucun fichier</p>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {typeEntries.map(({ key, count, color, bg, border, Icon, label }) => (
                  <div
                    key={key}
                    title={`${count} fichier${count > 1 ? 's' : ''} ${label}`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      padding: '4px 9px', borderRadius: 8,
                      background: bg, border: `1px solid ${border}`,
                      fontSize: 12, fontWeight: 600, color,
                      lineHeight: 1,
                    }}
                  >
                    <Icon size={12} strokeWidth={2.2} />
                    <span style={{ opacity: 0.85 }}>{label}</span>
                    <span style={{
                      background: color, color: 'white',
                      padding: '1px 6px', borderRadius: 10,
                      fontSize: 10, fontWeight: 700, minWidth: 16,
                      textAlign: 'center',
                    }}>{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
})()}

{/* Filtres projets */}
<div style={{
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16,
  padding: 16, background: 'var(--input-bg)', borderRadius: 10, border: '1px solid var(--card-border)'
}}>
  <div>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Type fichier</label>
    <select value={filters.category} onChange={(e) => setFilters(prev => ({...prev, category: e.target.value}))}
      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text)', fontSize: 13 }}>
      <option value="all">Tous types</option>
      <option value="Doc">Documents</option>
      <option value="Txt">Texte</option>
      <option value="Data">Données</option>
      <option value="Img">Images</option>
      <option value="Autre">Autres</option>
    </select>
  </div>
  <div>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Stockage</label>
    <select value={filters.storage} onChange={(e) => setFilters(prev => ({...prev, storage: e.target.value}))}
      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text)', fontSize: 13 }}>
      <option value="all">Tous</option>
      <option value="nextcloud">ITRIIA Cloud</option>
      <option value="local">Local</option>
    </select>
  </div>
  <div>
    <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Recherche</label>
    <input type="text" placeholder="Nom de fichier..."
      value={filters.search} onChange={(e) => setFilters(prev => ({...prev, search: e.target.value}))}
      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text)', fontSize: 13 }} />
  </div>
</div>

    {/* Grille fichiers + dossiers */}
    {loading ? (
      <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-secondary)' }}>
        <div style={{
          width: 32, height: 32, border: '2px solid var(--card-border)', borderTop: '2px solid #3b82f6',
          borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px'
        }} />
        Chargement des fichiers projets...
      </div>
    ) : (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>

        {/* Bouton retour parent */}
        {currentFolderId !== null && (
          <div onClick={navigateUp} style={{
            gridColumn: '1 / -1', padding: '10px 16px',
            background: 'var(--input-bg)', borderRadius: 10,
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
            fontSize: 12, color: 'var(--text-secondary)',
            border: '1px solid var(--card-border)',
            transition: 'all 0.15s'
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.color = '#3b82f6'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            <ArrowLeft size={14} /> Dossier parent
          </div>
        )}

        {/* Folder cards */}
        {subfolders.map(folder => (
          <div key={`folder-${folder.id}`}
            onClick={() => navigateToFolder(folder.id)}
            style={{
              background: 'var(--card-bg)', border: '1px solid var(--card-border)',
              borderRadius: 12, padding: 16, cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex', alignItems: 'center', gap: 12
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(59,130,246,0.08)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--card-border)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <div style={{
              width: 44, height: 44, borderRadius: 10,
              background: 'rgba(59,130,246,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>
              <FolderOpen size={22} style={{ color: '#3b82f6' }} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {folder.name}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                {folder.file_count || 0} fichier{(folder.file_count || 0) !== 1 ? 's' : ''}
                {folder.total_file_count && folder.total_file_count !== folder.file_count && (
                  <span style={{ opacity: 0.6 }}> ({folder.total_file_count} au total)</span>
                )}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          </div>
        ))}

        {/* File cards */}
        {filteredProjectFiles.map((file) => (
          <div key={file.id} className="file-card-center" style={{
            background: 'var(--card-bg)', border: '1px solid var(--card-border)',
            borderRadius: 12, padding: 16, transition: 'box-shadow 0.2s'
          }}
            onMouseEnter={e => {
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.1)';
              const actions = e.currentTarget.querySelector('.file-card-actions');
              if (actions) actions.style.opacity = '1';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.boxShadow = 'none';
              const actions = e.currentTarget.querySelector('.file-card-actions');
              if (actions) actions.style.opacity = '0';
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ fontWeight: 500, color: 'var(--text)', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{file.name}</h4>
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>{file.size} • {file.upload_date}</p>
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 8 }}>
                <span style={{
                  padding: '2px 8px', fontSize: 10, borderRadius: 12,
                  background: 'rgba(37,99,235,0.1)', color: '#3b82f6'
                }}>
                  {file.category}
                </span>
                <span style={{
                  padding: '2px 8px', fontSize: 10, borderRadius: 12,
                  background: file.storage === 'nextcloud' ? 'rgba(34,197,94,0.1)' : 'rgba(107,114,128,0.1)',
                  color: file.storage === 'nextcloud' ? '#22c55e' : '#6b7280'
                }}>
                  {file.storage === 'nextcloud' ? 'ITRIIA Cloud' : 'Local'}
                </span>
                {file.isShared && (
                  <span style={{
                    padding: '2px 8px', fontSize: 10, borderRadius: 12,
                    background: 'rgba(139,92,246,0.1)', color: '#8b5cf6',
                    display: 'inline-flex', alignItems: 'center', gap: 3
                  }}>
                    <Share2 size={9} /> Partagé
                  </span>
                )}
              </div>
            </div>

            <div className="file-card-actions" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', opacity: 0, transition: 'opacity 0.18s ease' }}>
              <FileActionButtons
                file={file}
                projectId={projectId}
                canShare={['admin_general', 'oracle_fondateur'].includes(user?.role)}
                onPreview={(f) => setPreviewFile({ id: f.id, name: f.name })}
                onAnalyze={(f) => onFileAnalyze && onFileAnalyze(f.id, f.name, 'analyze')}
                onChanged={() => { loadProjectFiles(); if (onFilesChanged) onFilesChanged(); }}
              />
            </div>
          </div>
        ))}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            gridColumn: '1 / -1', marginTop: 8, padding: 16,
            background: 'var(--input-bg)', borderRadius: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Page {currentPage} sur {totalPages} • {totalFiles} fichiers total
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1}
                style={{
                  padding: '6px 14px', fontSize: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: 6, cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1,
                  color: 'var(--text)'
                }}>Précédent</button>
              <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages}
                style={{
                  padding: '6px 14px', fontSize: 12, background: 'var(--card-bg)', border: '1px solid var(--card-border)',
                  borderRadius: 6, cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1,
                  color: 'var(--text)'
                }}>Suivant</button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {filteredProjectFiles.length === 0 && subfolders.length === 0 && !loading && (
          <div style={{
            gridColumn: '1 / -1', textAlign: 'center', padding: '40px 0',
            color: 'var(--text-secondary)', fontSize: 13
          }}>
            {currentFolderId ? 'Ce dossier est vide' : 'Aucun fichier dans ce projet'}
          </div>
        )}
      </div>
    )}
  </div>
)}

        {activeTab === 'upload' && (
          <UploadPasteTab
            projectId={projectId}
            user={user}
            selectedFiles={selectedFiles}
            setSelectedFiles={setSelectedFiles}
            uploadFolderId={uploadFolderId}
            setUploadFolderId={setUploadFolderId}
            uploading={uploading}
            handleUpload={handleUpload}
            uploadProgress={uploadProgress}
          />
        )}
      </div>

      {/* Modal Preview */}
      {previewFile && (
        <FilePreviewModal
          projectId={projectId}
          fileId={previewFile.id}
          fileName={previewFile.name}
          onClose={() => setPreviewFile(null)}
          onAnalyze={(fId, fName, mode) => {
            setPreviewFile(null);
            if (onFileAnalyze) onFileAnalyze(fId, fName, mode);
          }}
        />
      )}
    </div>
  );
};

export default FilesInterface;
